import styles from './mailing-lists.module.css'

import { LuEllipsis } from '@react-icons/all-files/lu/LuEllipsis'
import type { ColumnDef } from '@tanstack/react-table'
import orderBy from 'lodash/orderBy'
import React, { useCallback, useMemo, useState } from 'react'
import { Link as RouterLink, Outlet, useNavigate } from 'react-router-dom'

import { exportEmailGroup } from '@disco/state-management/actions/EmailGroupActions'
import { getUser } from '@disco/state-management/selectors/user'
import dayjs from '@disco/utils/dayjs'

import type { MailingList, RecipientListId } from 'types/list'

import EmptyMessage from 'components/common/empty-message'
import DocumentHeadTags from 'components/document-head-tags'
import { Button, DataTable, Flex, Link, Spinner } from 'components/ds'
import type { RowAction } from 'components/ds/DataTable'
import DownloadModal from 'components/modals/download-modal'
import PageHeader from 'components/ui/page-header'

import { useDispatch, useSelector } from 'hooks/use-redux'

import {
  CAPTION_EXISTING,
  CAPTION_EXPORT,
  CAPTION_IMPORT,
  CAPTION_SINGLE,
} from '../add/buttons'
import IconEmptyLists from './icon-empty-lists'

const ContactsMailingLists = (): null | React.ReactNode => {
  const emailGroups = useSelector(state => state.emailGroups)
  const isAdmin = useSelector(getUser)?.is_admin
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [deferred, setDeferred] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exportId, setExportId] = useState<RecipientListId | null>(null)

  const exportClick = useCallback(
    async (id: RecipientListId) => {
      setLoading(true)
      setError(false)
      setDeferred(false)
      setDownloadUrl(null)
      setModalOpen(true)
      const result: Record<string, any> = await dispatch(exportEmailGroup(id))

      if (result?.deferred) {
        setDeferred(true)
        setLoading(false)
      }

      if (result?.download_url) {
        setDownloadUrl(result?.download_url)
        setLoading(false)
      }

      if (result?.error) {
        setError(true)
        setLoading(false)
      }
    },
    [dispatch],
  )

  const closeModal = () => setModalOpen(false)

  const renderModal = () => {
    if (!modalOpen) return null

    if (downloadUrl) {
      return (
        <DownloadModal type="base" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (loading) {
      return (
        <DownloadModal type="loading" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (error && exportId) {
      return (
        <DownloadModal
          type="error"
          onClose={closeModal}
          onClick={() => exportClick(exportId)}
        />
      )
    }

    if (deferred) {
      return <DownloadModal type="deferred" onClose={closeModal} />
    }
  }

  const columns = useMemo<ColumnDef<MailingList>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableResizing: false,
        cell: ({ row }) => (
          <Link asChild color="gray" highContrast underline="hover">
            <RouterLink to={`/lists/${row.original.id}`}>
              <span translate="no">{row.original.name}</span>
            </RouterLink>
          </Link>
        ),
      },
      {
        accessorKey: 'count_recipients',
        header: 'Contacts',
        enableResizing: false,
        cell: ({ row }) => (
          <Link asChild color="gray" highContrast underline="hover">
            <RouterLink to={`/lists/${row.original.id}`}>
              {row.original.count_recipients ?? 0}
            </RouterLink>
          </Link>
        ),
      },
      {
        accessorKey: 'created',
        header: 'Created',
        enableResizing: false,
        cell: ({ row }) => (
          <span
            title={dayjs(row.original.created).format('MMMM Do YYYY, h:mma')}
          >
            {dayjs(row.original.created).format('MMMM Do YYYY')}
          </span>
        ),
      },
    ],
    [],
  )

  const getRowActions = useCallback(
    (list: MailingList): RowAction[] => [
      {
        label: 'Actions',
        icon: <LuEllipsis size={16} />,
        subActions: [
          {
            label: CAPTION_EXISTING,
            onClick: () => navigate(`/contacts/list?append=${list.id}`),
          },
          {
            label: CAPTION_IMPORT,
            onClick: () => navigate(`/lists/${list.id}/file-import`),
          },
          {
            label: CAPTION_SINGLE,
            onClick: () => navigate(`/lists/${list.id}/add-single`),
          },
          {
            label: CAPTION_EXPORT,
            disabled: !isAdmin,
            tooltip: isAdmin ? undefined : 'Only admins can export contacts.',
            onClick: () => {
              setExportId(list.id)
              exportClick(list.id)
            },
          },
          {
            label: 'Delete',
            color: 'red',
            onClick: () => navigate(`/lists/delete-list/${list.id}`),
          },
        ],
      },
    ],
    [exportClick, isAdmin, navigate],
  )

  const mailingLists = useMemo(
    () => orderBy(emailGroups.mailingList, 'created', 'desc'),
    [emailGroups.mailingList],
  )

  if (emailGroups.isFetchingMailingLists) {
    return (
      <Flex justify="center" mt="9">
        <Spinner size="3" />
      </Flex>
    )
  }
  if (!emailGroups.mailingListsFetched) return null
  const createNewListButton = (
    <Button onClick={() => navigate('/lists/create')}>Create new list</Button>
  )
  let content

  if (mailingLists.length > 0) {
    content = (
      <div className={styles.table}>
        <DataTable
          data={mailingLists}
          columns={columns}
          getRowActions={getRowActions}
          alwaysShowRowActions
          size="2"
        />
      </div>
    )
  } else {
    content = (
      <div className={styles.emptyMessage}>
        <EmptyMessage
          icon={<IconEmptyLists />}
          header="No mailing lists yet"
          cta={
            <div>
              Create your first mailing list by clicking the button below.
              <div className={styles.emptyButtonContainer}>
                {createNewListButton}
              </div>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <>
      <DocumentHeadTags title="Mailing Lists | DISCO" />
      <PageHeader title="Mailing Lists" actions={createNewListButton} />
      {content}
      <Outlet />
      {renderModal()}
    </>
  )
}

export default ContactsMailingLists
